import React, { useEffect } from 'react';
import { FormikValues } from 'formik';

import {
  adminActions,
  adminIgnoreData,
  adminIgnoreRequest,
  adminResetValue,
  getIgnoreDetails,
  resetStatus,
  successSelector,
  AdminLegendVariant,
  PermissionsOfAdmin,
  PermissionsRemovalKeys,
  ServiceTypes,
  adminSelfIgnoreRequest,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';

import { renderScreen } from '../../actions/drawerActions';
import InputModal from '../../components/Modals/InputModal';
import { OtherScreens, ScreenTypes } from '../../helpers/Constants';
import strings from '../../localization';
import { setDialog } from '../../reducers/dialogReducer';
import { genericFunction } from './useAvailableWorkshiftActions';
import { ignoreForm } from '../../components/Admin/RequestedPharmacistList/helpers';
import styles from '../../components/Admin/RequestedPharmacistList/PharmacistList/styles';
import { getDayMonthDateYear } from '../../components/Reports/helpers';

import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import { serverToday } from '../../helpers/Functions';
import { removeBasedOnPermissions } from '../../helpers/permissionsHelper';
import useAdminPermissions from '../Admin/useAdminPermissions';
import AdminIgnoreContractModal from '../../components/Admin/RequestedPharmacistList/AdminIgnoreContractModal';

const { self, full } = ServiceTypes;
const isSelfService = self === ServiceHandler.getService();
const syncKeys = { matching: 'matching', approve: 'approve', ignore: 'ignore' };
const syncDisableKeys = [
  syncKeys.matching,
  PermissionsRemovalKeys.ExpressBooking,
];
const externalUserKeys = [
  syncKeys.approve,
  syncKeys.ignore,
  syncKeys.matching,
  PermissionsRemovalKeys.ExpressBooking,
];

interface IGoToApproveBooking {
  bookingId: string;
  workshiftId: string;
  fromScreen: OtherScreens;
  eventIds?: Array<string> | null;
  longTermShiftContractId?: string;
  pharmacistId?: string;
}

interface IViewDetails {
  bookingId: string;
  workshiftId: string;
  pharmacyId: string;
  longTermShiftContractId?: string;
  pharmacistId?: string;
}

export const menuActions = (
  viewDetailsAction: genericFunction,
  ignoreAction: genericFunction,
  approveAction: genericFunction,
  matchingAvailabilities: genericFunction,
  expressBookingAction: genericFunction,
) =>
  [
    {
      key: '1',
      label: strings.viewDetails,
      onClick: viewDetailsAction,
    },
    {
      key: isSelfService
        ? PermissionsRemovalKeys.ApproveBooking
        : syncKeys.approve,
      label: strings.approve,
      onClick: approveAction,
    },
    {
      key: isSelfService
        ? PermissionsRemovalKeys.ApproveBooking
        : syncKeys.ignore,
      label: strings.ignore,
      onClick: ignoreAction,
    },
    {
      key: syncKeys.matching,
      label: strings.matchingAvailabilities,
      onClick: matchingAvailabilities,
    },
    {
      key: PermissionsRemovalKeys.ExpressBooking,
      label: strings.expressBooking,
      onClick: expressBookingAction,
    },
  ];

const useRequestedPharmacistActions = (
  isAutoSyncRecord?: boolean,
  externalPharmacistId?: string,
) => {
  const dispatch = useAppDispatch();
  const ignoreDetails = useAppSelector(adminIgnoreData);
  const today = serverToday().toISO({ includeOffset: false }) ?? '';
  const { can } = useAdminPermissions();

  const getIgnoreDetailSuccess = useAppSelector((state) =>
    successSelector([adminActions.getIgnoreRequestDetails], state));
  const {
    startDate,
    pharmacistName,
    pharmacyName,
    bookingId: id,
  } = ignoreDetails ?? {};

  const openCancelContractRequestModal = (longTermContractId: string, professionalId: string) =>
    () => {
      dispatch(
        setDialog({
          heading: {
            title: strings.ignore,
          },
          Component: (
            <AdminIgnoreContractModal
              successAction={adminActions.adminRequestContractDetails}
              longTermContractId={longTermContractId}
              professionalId={professionalId}
            />
          ),
          maxWidth: 'lg',
          showCloseButton: true,
        }),
      );
    };

  const ignore = (values: FormikValues) => {
    const ignoreAPIConfig = {
      [self]: adminSelfIgnoreRequest,
      [full]: adminIgnoreRequest,
    };

    const ignoreRequest = ignoreAPIConfig[ServiceHandler.getService()];

    dispatch(
      ignoreRequest({
        bookingId: id,
        note: values.note,
        sendEmailToPharmacist: values.sendEmailToPharmacist,
        isAutoSyncRecord,
      }),
    );
  };

  const initialValues = {
    startDate: getDayMonthDateYear(startDate),
    note: '',
    pharmacyName,
    pharmacistName,
    sendEmailToPharmacist: true,
    loader: ignoreDetails.bookingId,
  };

  const viewDetailsAction = ({
    bookingId,
    workshiftId,
    pharmacyId,
    pharmacistId,
    longTermShiftContractId,
  }: IViewDetails) =>
    () => {
      dispatch(
        renderScreen({
          screenNumber: 3,
          screenType: ScreenTypes.requestedList,
          eventId: bookingId,
          workshiftId,
          pharmacyId,
          pharmacistId,
          extraParameters: {
            isAutoSyncRecord,
            externalPharmacistId,
            longTermShiftContractId,
          },
        }),
      );
    };

  const goToApproveBooking = ({
    longTermShiftContractId,
    eventIds,
    bookingId,
    workshiftId,
    fromScreen,
    pharmacistId,
  }: IGoToApproveBooking) =>
    () => {
      dispatch(
        renderScreen({
          screenNumber: 4,
          screenType: ScreenTypes.requestedList,
          eventId: bookingId,
          workshiftId,
          eventIds: eventIds ?? [],
          fromScreen,
          pharmacistId,
          extraParameters: {
            isAutoSyncRecord,
            longTermShiftContractId,
          },
        }),
      );
    };

  const goToMatchingAvailabilities = (workshiftId: string) =>
    () => {
      dispatch(
        renderScreen({
          screenType: ScreenTypes.availableWorkshift,
          screenNumber: 4,
          eventId: workshiftId,
          type: AdminLegendVariant.requested,
        }),
      );
    };

  const goToExpressBooking = (bookingId: string, workshiftId: string) =>
    () => {
      dispatch(
        renderScreen({
          screenType: ScreenTypes.availableWorkshift,
          screenNumber: 5,
          eventId: bookingId,
          workshiftId,
          type: AdminLegendVariant.requested,
        }),
      );
    };

  useEffect(() => {
    if (getIgnoreDetailSuccess && initialValues.loader) {
      dispatch(
        setDialog({
          Component: (
            <InputModal
              submitLabel={strings.ignore}
              onSubmit={ignore}
              initialValues={initialValues}
              config={ignoreForm}
            />
          ),
          heading: {
            title: strings.ignoreWorkshift,
          },
          customStyle: {
            customTitleContainer: styles.modalMargin,
          },
          showCloseButton: true,
        }),
      );
      dispatch(adminResetValue(['adminIgnoreData']));
      dispatch(resetStatus([adminActions.getIgnoreRequestDetails]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues.loader, getIgnoreDetailSuccess]);

  const ignoreRequestAction = (bookingId: string) =>
    () => {
      dispatch(getIgnoreDetails({ id: bookingId, isAutoSyncRecord }));
    };

  const actions = (
    bookingId: string,
    workshiftid: string,
    date: string,
    pharmacyId: string,
    longTermShiftContractId: string,
    pharmacistId: string,
  ) => {
    let menu = menuActions(
      viewDetailsAction({
        bookingId,
        workshiftId: workshiftid,
        pharmacyId,
        longTermShiftContractId,
        pharmacistId,
      }),
      longTermShiftContractId
        ? openCancelContractRequestModal(longTermShiftContractId, pharmacistId)
        : ignoreRequestAction(bookingId),
      goToApproveBooking({
        bookingId,
        workshiftId: workshiftid,
        fromScreen: OtherScreens.AdminCalendar,
        longTermShiftContractId,
        pharmacistId,
      }),
      goToMatchingAvailabilities(workshiftid),
      goToExpressBooking(bookingId, workshiftid),
    );
    if (date < today) {
      menu = menu.slice(0, 1);
    }

    menu = removeBasedOnPermissions(menu, [PermissionsOfAdmin.Booking], can);

    if (isAutoSyncRecord) {
      menu = menu.filter((item) =>
        !syncDisableKeys.includes(item.key));
    }

    if (externalPharmacistId) {
      menu = menu.filter((item) =>
        !externalUserKeys.includes(item.key));
    }

    return menu;
  };

  return {
    actions,
    ignoreRequestAction,
    goToApproveBooking,
    viewDetailsAction,
  };
};

export default useRequestedPharmacistActions;
